"use client";

import {
  Check,
  ChevronLeft,
  ChevronRight,
  LoaderCircle,
  Maximize,
  Minimize,
  Pause,
  PictureInPicture2,
  Play,
  RotateCcw,
  RotateCw,
  Settings,
  Volume1,
  Volume2,
  VolumeX,
} from "lucide-react";
import { createContext, useCallback, useEffect, useEffectEvent, useRef, useState, useSyncExternalStore } from "react";
import { useUi } from "@/components/ui/use-ui";
import { FRAME_POINTER_EVENT, FrameMedia, type FrameEventDetail } from "@/components/video/frame-media";
import type { VideoSummary } from "@/lib/api/types";
import { thumbBackground } from "@/lib/art";
import { cn } from "@/lib/cn";
import { duration as fmt } from "@/lib/format";
import { fmt as fill } from "@/lib/locales/fmt";
import { addWatchTime, setPlaying as setActivityPlaying } from "@/lib/activity";
import { recordProgress, useResumePosition } from "@/lib/watching";

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];
// Placeholder ladder until the API returns real renditions.
const QUALITIES = ["Auto", "1080p", "720p", "480p", "240p"];
// Media events the player follows: a <video>'s own, plus those an /embed frame adds (see FrameMedia).
const MEDIA_EVENTS = ["play", "pause", "ended", "waiting", "playing", "canplay", "timeupdate", "loadedmetadata", "progress", "volumechange", "error"];
/** Whether the main pointer is a finger (a touch screen), and its changes (a tablet's keyboard). */
const COARSE = "(pointer: coarse)";
const isCoarse = () => window.matchMedia(COARSE).matches;
const subscribeCoarse = (onChange: () => void) => {
  const m = window.matchMedia(COARSE);
  m.addEventListener("change", onChange);
  return () => m.removeEventListener("change", onChange);
};

const FRAME_EVENTS = ["ready", "hello", "levels", "tap", "uitap", "dblclick", "pointer", "pointerleave", "key"];

type Media = HTMLVideoElement | FrameMedia;
/** Seconds of playback between Watching updates; pauses, the end and leaving the page save at once. */
const SAVE_EVERY = 5;

interface PlayerProps {
  /** Counts a view on first play. */
  videoId?: string;
  src?: string | null;
  /** Our /embed page streaming the video from its source site, played in a frame when there is no `src`. */
  frame?: string | null;
  poster?: string | null;
  /** Seeds the gradient poster when there is no image. */
  seed: string;
  title: string;
  /** Known length in seconds, shown before metadata loads. */
  duration: number;
  /** Listed under Watching with where playback stopped, and resumed from there next time. */
  summary?: VideoSummary;
  /** Drawn over the video (the ad before it, Preroll.tsx), which learns from PlayerStarted when it has started. */
  overlay?: React.ReactNode;
}

/** The second a link to the page asks to start at (?t=95), or 0. */
function linkedSecond(): number {
  const t = Number(new URLSearchParams(window.location.search).get("t"));
  return Number.isInteger(t) && t > 0 ? t : 0;
}
const noSubscription = () => () => {};

/** Whether the video has started playing, for what the player draws over it. */
export const PlayerStarted = createContext(false);

export function Player({ videoId, src, frame, poster, seed, title, duration, summary, overlay }: PlayerProps) {
  const t = useUi().player;
  const wrap = useRef<HTMLDivElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const frameEl = useRef<HTMLIFrameElement>(null);
  /** The <video>, or the handle on the one inside the frame. */
  const media = useRef<Media | null>(null);
  const bar = useRef<HTMLDivElement>(null);
  const fsButton = useRef<HTMLButtonElement>(null);
  /**
   * iPhone has no element full screen, only the native one of a <video>, entered inside a tap on it: a
   * framed video's is in the frame, so the frame lays its own invisible button over ours (frame.js).
   */
  const [fsInFrame, setFsInFrame] = useState(false);
  const hideTimer = useRef<number | undefined>(undefined);
  /**
   * Whether a mouse rests on the controls: they stay up then. Hidden, they let clicks through to the video,
   * so a second click on the mute button paused the video instead of turning the sound back on.
   */
  const overControls = useRef(false);
  /** The volume before muting, for unmuting a video whose volume was slid down to nothing. */
  const lastVolume = useRef(1);
  // Watching: whether it has played yet, where progress was last saved, and where to resume.
  const played = useRef(false);
  const savedAt = useRef(0);
  const resume = useRef(0);
  // Rankings: the playback position at the last timeupdate, to count seconds actually played.
  const lastPlayback = useRef(0);

  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState(!src && !frame);
  const [time, setTime] = useState(0);
  const [total, setTotal] = useState(duration);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [quality, setQuality] = useState("Auto");
  // Heights a framed stream can switch between, and the one it plays.
  const [levels, setLevels] = useState<number[]>([]);
  const [height, setHeight] = useState(0);
  // Until the frame's script runs, clicks on the frame would be lost, so the player takes them.
  const [frameReady, setFrameReady] = useState(false);
  const [startOver, setStartOver] = useState(false);
  /** Which video the time watched counts towards. */
  const watchId = summary?.id ?? videoId ?? seed;
  const suggested = useResumePosition(summary?.id ?? videoId);
  // A link to a moment (?t=95, the video page's SeekToAction for search engines' key moments).
  const linked = useSyncExternalStore(noSubscription, linkedSecond, () => 0);
  const linkedAt = linked > 0 && (!duration || linked < duration) ? linked : 0;
  /** Where the first play starts: the moment linked to, else where this visitor stopped last time, until they choose to start over. */
  const resumeAt = started || startOver ? 0 : linkedAt || suggested;
  const [menu, setMenu] = useState<null | "main" | "speed" | "quality">(null);
  const [controls, setControls] = useState(true);
  /**
   * A touch screen: once playing, a tap on the video shows or hides the controls rather than pausing
   * (pausing is the centre button's), as phone players do. The first tap still starts it.
   */
  const touch = useSyncExternalStore(subscribeCoarse, isCoarse, () => false);
  const [fullscreen, setFullscreen] = useState(false);
  const [hover, setHover] = useState<{ x: number; t: number } | null>(null);
  const [scrubbing, setScrubbing] = useState(false);
  const [flash, setFlash] = useState<null | "play" | "pause" | "back" | "fwd">(null);

  const v = () => media.current;

  const poke = useCallback(() => {
    setControls(true);
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => {
      if (!video.current?.paused && !overControls.current) setControls(false);
    }, 2600);
  }, []);

  /** Sound off, or back on at the volume it had (a volume slid to nothing comes back too). */
  const toggleMute = useCallback(() => {
    const el = media.current;
    if (!el) return;
    if (el.muted || el.volume === 0) {
      if (el.volume === 0) el.volume = lastVolume.current;
      el.muted = false;
    } else el.muted = true;
  }, []);

  const toggle = useCallback(() => {
    const el = v();
    if (!el || error) return;
    if (el.paused) {
      el.play().catch((e: DOMException) => e.name !== "NotAllowedError" && e.name !== "AbortError" && setError(true));
      setFlash("play");
    } else {
      el.pause();
      setFlash("pause");
    }
    poke();
  }, [error, poke]);

  const seekBy = useCallback(
    (delta: number) => {
      const el = v();
      if (!el) return;
      el.currentTime = Math.min(Math.max(0, el.currentTime + delta), el.duration || total);
      setFlash(delta < 0 ? "back" : "fwd");
      poke();
    },
    [poke, total],
  );

  const toggleFullscreen = useCallback(() => {
    const node = wrap.current;
    if (!node) return;
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else if (node.requestFullscreen) node.requestFullscreen().catch(() => {});
    else (v() as Media & { webkitEnterFullscreen?: () => void })?.webkitEnterFullscreen?.();
  }, []);

  const setVol = (next: number) => {
    const el = v();
    if (!el) return;
    el.volume = Math.min(1, Math.max(0, next));
    el.muted = el.volume === 0;
  };

  const saveProgress = useEffectEvent((now: boolean) => {
    const el = media.current;
    if (!summary || !el || !played.current) return;
    savedAt.current = el.currentTime;
    recordProgress(summary, el.currentTime, Number.isFinite(el.duration) && el.duration > 0 ? el.duration : total, now);
  });

  const onMediaEvent = useEffectEvent((type: string, detail?: FrameEventDetail) => {
    const el = media.current;
    if (!el) return;
    switch (type) {
      case "play":
        if (!started && videoId) navigator.sendBeacon?.(`/api/videos/${videoId}/view`);
        lastPlayback.current = el.currentTime;
        setActivityPlaying(watchId, true);
        played.current = true;
        saveProgress(false);
        setPlaying(true);
        setStarted(true);
        poke();
        break;
      case "pause":
        setActivityPlaying(watchId, false);
        saveProgress(true);
        setPlaying(false);
        setControls(true);
        break;
      case "ended":
        setActivityPlaying(watchId, false);
        saveProgress(true);
        setPlaying(false);
        break;
      case "waiting":
        setWaiting(true);
        break;
      case "playing":
      case "canplay":
        setWaiting(false);
        break;
      case "timeupdate": {
        // Normal playback moves a fraction of a second between updates; a seek jumps further and isn't watching.
        const advanced = el.currentTime - lastPlayback.current;
        if (!el.paused && advanced > 0 && advanced < 3) addWatchTime(watchId, advanced / (el.playbackRate || 1));
        lastPlayback.current = el.currentTime;
        if (!scrubbing) setTime(el.currentTime);
        if (Math.abs(el.currentTime - savedAt.current) >= SAVE_EVERY) saveProgress(false);
        break;
      }
      case "loadedmetadata":
        if (Number.isFinite(el.duration)) setTotal(el.duration);
        break;
      case "progress":
        if (el.buffered.length) setBuffered(el.buffered.end(el.buffered.length - 1));
        break;
      case "volumechange":
        if (el.volume > 0) lastVolume.current = el.volume;
        setVolume(el.volume);
        setMuted(el.muted);
        break;
      case "error":
        setError(true);
        break;
      // From the frame: its state once it answers, then what happens inside it.
      case "ready":
      case "hello":
        setFrameReady(true);
        setPlaying(!el.paused);
        // Before the first play, a seek only sets where playback will start.
        if (!played.current && resume.current > 0) el.currentTime = resume.current;
        break;
      case "levels":
        if (el instanceof FrameMedia) {
          setLevels(el.heights);
          setHeight(el.height);
          setQuality(el.autoQuality || !el.height ? "Auto" : `${el.height}p`);
        }
        break;
      case "tap":
        setFlash(detail?.playing ? "play" : "pause");
        poke();
        break;
      case "uitap":
        tapUi();
        break;
      case "dblclick":
        toggleFullscreen();
        break;
      case "pointer":
        poke();
        window.dispatchEvent(new Event(FRAME_POINTER_EVENT));
        break;
      case "pointerleave":
        // Leaving the frame for the controls over it isn't leaving the player.
        if (playing && !menu && !overControls.current) setControls(false);
        break;
      case "key":
        if (detail?.key) onKey(detail.key);
        break;
    }
  });

  useEffect(() => {
    const iframe = frameEl.current;
    // Set here, not rendered: streamed HTML moves the player into place, and a moved frame loads again.
    if (iframe && frame && iframe.getAttribute("src") !== frame) iframe.src = frame;
    const el: Media | null = iframe ? new FrameMedia(iframe) : video.current;
    if (!el) return;
    media.current = el;
    const listen = (e: Event) => onMediaEvent(e.type, (e as CustomEvent<FrameEventDetail>).detail);
    const types = el instanceof FrameMedia ? [...MEDIA_EVENTS, ...FRAME_EVENTS] : MEDIA_EVENTS;
    for (const type of types) el.addEventListener(type, listen);

    // Closing the tab or switching away saves where playback stands.
    const leave = () => saveProgress(true);
    const hidden = () => document.visibilityState === "hidden" && leave();
    window.addEventListener("pagehide", leave);
    document.addEventListener("visibilitychange", hidden);
    return () => {
      leave();
      setActivityPlaying(watchId, false);
      // Navigating away keeps the route mounted but hidden (React Activity), so stop playback.
      el.pause();
      setPlaying(false);
      window.removeEventListener("pagehide", leave);
      document.removeEventListener("visibilitychange", hidden);
      for (const type of types) el.removeEventListener(type, listen);
      if (el instanceof FrameMedia) el.dispose();
    };
  }, [frame, watchId]);

  // Before the first play a seek only sets where playback will start. A frame that isn't ready yet
  // takes it when it is (see "ready" above).
  useEffect(() => {
    resume.current = resumeAt;
    const el = media.current;
    if (el && !played.current && (resumeAt > 0 || el.currentTime > 0)) el.currentTime = resumeAt;
  }, [resumeAt]);

  useEffect(() => {
    const onFs = () => setFullscreen(document.fullscreenElement === wrap.current);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  useEffect(() => {
    if (!flash) return;
    const t = window.setTimeout(() => setFlash(null), 450);
    return () => window.clearTimeout(t);
  }, [flash]);

  useEffect(() => () => window.clearTimeout(hideTimer.current), []);

  /** Shortcut keys, pressed on the page or inside the frame. Returns whether the key was one. */
  function onKey(raw: string): boolean {
    const el = media.current;
    if (!el) return false;
    const key = raw.toLowerCase();
    if (key === " " || key === "k") toggle();
    else if (key === "f") toggleFullscreen();
    else if (key === "m") toggleMute();
    else if (key === "arrowleft" || key === "j") seekBy(key === "j" ? -10 : -5);
    else if (key === "arrowright" || key === "l") seekBy(key === "l" ? 10 : 5);
    else if (key === "arrowup") el.volume = Math.min(1, el.volume + 0.1);
    else if (key === "arrowdown") el.volume = Math.max(0, el.volume - 0.1);
    else if (/^[0-9]$/.test(key) && el.duration) el.currentTime = (el.duration * Number(key)) / 10;
    else return false;
    poke();
    return true;
  }
  const onPageKey = useEffectEvent((e: KeyboardEvent) => {
    const target = e.target as HTMLElement | null;
    if (e.metaKey || e.ctrlKey || e.altKey || target?.closest("input, textarea, select, [contenteditable], [role=dialog]")) return;
    if (onKey(e.key)) e.preventDefault();
  });

  useEffect(() => {
    const listen = (e: KeyboardEvent) => onPageKey(e);
    window.addEventListener("keydown", listen);
    return () => window.removeEventListener("keydown", listen);
  }, []);

  const timeAt = (clientX: number) => {
    const rect = bar.current!.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    // x is clamped so the time tooltip never overflows the player edges.
    return { x: Math.min(Math.max(ratio * rect.width, 24), rect.width - 24), t: ratio * (total || 0) };
  };

  const onBarDown = (e: React.PointerEvent) => {
    if (!total) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setScrubbing(true);
    const { t } = timeAt(e.clientX);
    setTime(t);
    if (v()) v()!.currentTime = t;
  };
  const onBarMove = (e: React.PointerEvent) => {
    const at = timeAt(e.clientX);
    setHover(at);
    if (scrubbing) {
      setTime(at.t);
      if (v()) v()!.currentTime = at.t;
    }
    poke();
  };
  const onBarUp = () => setScrubbing(false);

  const pct = total ? (time / total) * 100 : 0;
  const bufPct = total ? (buffered / total) * 100 : 0;
  const showUi = controls || !playing || menu !== null || scrubbing;

  /** A tap on a playing video on a touch screen: the controls come up, or go if they're up. */
  const tapUi = () => {
    if (showUi && playing) {
      window.clearTimeout(hideTimer.current);
      setControls(false);
    } else poke();
  };

  // A framed video's taps are the frame's (frame.js): once it has played, they go to the controls too.
  useEffect(() => {
    const el = media.current;
    if (el instanceof FrameMedia) el.setUiTaps(touch && started);
  }, [touch, started]);

  useEffect(() => {
    if (frame) setFsInFrame(!document.fullscreenEnabled && !wrap.current?.requestFullscreen);
  }, [frame]);

  // Tells the frame where our full screen button is while it shows, for its own to sit over it.
  useEffect(() => {
    const el = media.current;
    const button = fsButton.current;
    if (!fsInFrame || !(el instanceof FrameMedia) || !button || !wrap.current) return;
    const place = () => {
      const b = button.getBoundingClientRect();
      const w = wrap.current!.getBoundingClientRect();
      el.setFullscreenArea(showUi && started ? { x: b.left - w.left, y: b.top - w.top, width: b.width, height: b.height } : null);
    };
    place();
    const ro = new ResizeObserver(place);
    ro.observe(wrap.current);
    return () => ro.disconnect();
  }, [fsInFrame, showUi, started]);

  useEffect(() => {
    if (media.current instanceof FrameMedia) media.current.setCursorHidden(!showUi);
  }, [showUi]);
  const VolumeIcon = muted || volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;
  const iconBtn = "grid size-10 place-items-center rounded-full text-white/90 transition-colors hover:bg-white/15 hover:text-white";

  return (
    <div
      ref={wrap}
      onPointerMove={poke}
      // A finger "leaves" after every tap: only a mouse leaving hides the controls (a tap would hide them
      // before it reached the button it was meant for).
      onPointerLeave={(e) => e.pointerType === "mouse" && playing && !menu && setControls(false)}
      className={cn(
        "theme-dark group/player relative aspect-video w-full overflow-hidden bg-black select-none max-md:-mx-3 max-md:w-[calc(100%+1.5rem)] md:rounded-xl",
        fullscreen ? "rounded-none" : "md:max-w-[calc((100svh-190px)*16/9)]",
        !showUi && "cursor-none",
      )}
    >
      {frame ? (
        // Holds just the video; clicks on it reach the frame, which plays inside the click (see FrameMedia).
        // Sandboxed without allow-same-origin: the source's CDN refuses our origin but serves "Origin: null".
        <iframe
          ref={frameEl}
          title={title}
          sandbox="allow-scripts"
          // "*": sandboxed, the frame's page has no origin of its own for a bare "fullscreen" to match.
          allow="autoplay *; fullscreen *; picture-in-picture *"
          allowFullScreen
          className="size-full border-0 bg-black"
        />
      ) : (
        <video
          ref={video}
          src={src ?? undefined}
          poster={poster ?? undefined}
          playsInline
          preload="metadata"
          aria-label={title}
          className="size-full bg-black object-contain"
          onClick={touch && started ? tapUi : toggle}
          onDoubleClick={toggleFullscreen}
        />
      )}

      {frame && !frameReady && <div aria-hidden onClick={toggle} className="absolute inset-0" />}

      {/* Gradient poster until the first frame plays (no thumbnails before the API). */}
      {!started && !poster && (
        <div
          aria-hidden
          onClick={toggle}
          className={cn("absolute inset-0 animate-drift", frame && "pointer-events-none")}
          style={{ backgroundImage: thumbBackground(seed) }}
        />
      )}

      {/* Above the gradient poster; the center overlay passes clicks through. */}
      {resumeAt > 0 && !started && !error && (
        <div className="absolute bottom-3 left-3 flex items-center gap-2 text-[13px] md:bottom-4 md:left-4">
          <span className="glass rounded-full px-3 py-1.5 text-white">{fill(t.resumesAt, { at: fmt(resumeAt) })}</span>
          <button
            type="button"
            onClick={() => setStartOver(true)}
            className="glass rounded-full px-3 py-1.5 text-soft transition-colors hover:text-white"
          >
            {t.startOver}
          </button>
        </div>
      )}

      {/* Center: big play / spinner / transient action flash */}
      <div className="pointer-events-none absolute inset-0 grid place-items-center">
        {error ? (
          <div className="glass rounded-xl px-5 py-4 text-center text-[14px] text-soft">
            {t.unavailable}
          </div>
        ) : waiting && playing ? (
          <LoaderCircle size={56} strokeWidth={1.2} className="animate-spin text-white/90" />
        ) : !playing ? (
          <button
            type="button"
            aria-label={t.play}
            onClick={toggle}
            // Over a frame, the first click falls through to it so playback starts inside the click.
            className={cn(
              "glass grid size-[72px] place-items-center rounded-full text-white transition-transform hover:scale-105 md:size-20",
              frame && !started ? "pointer-events-none" : "pointer-events-auto",
            )}
          >
            <Play size={32} className="translate-x-0.5 fill-white" />
          </button>
        ) : touch && showUi ? (
          // On a touch screen a tap on the video brings the controls up; pausing is this button's.
          <button
            type="button"
            aria-label={t.pause}
            onClick={toggle}
            className="glass pointer-events-auto grid size-[72px] place-items-center rounded-full text-white md:size-20"
          >
            <Pause size={30} className="fill-white" />
          </button>
        ) : flash ? (
          <span key={flash} className="glass grid size-16 animate-[flash_0.45s_ease-out_forwards] place-items-center rounded-full text-white">
            {flash === "play" ? <Play size={26} className="fill-white" /> : flash === "pause" ? <Pause size={26} className="fill-white" /> : flash === "back" ? <RotateCcw size={24} /> : <RotateCw size={24} />}
          </span>
        ) : null}
      </div>

      {/* Bottom controls */}
      <div
        onPointerEnter={(e) => {
          if (e.pointerType === "mouse") overControls.current = true;
        }}
        onPointerLeave={() => {
          overControls.current = false;
          poke();
        }}
        className={cn(
          "absolute inset-x-0 bottom-0 bg-linear-to-t from-black/80 via-black/30 to-transparent px-3 pb-2 pt-14 transition-opacity duration-300 md:px-4",
          showUi && started ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      >
        <div
          ref={bar}
          role="slider"
          tabIndex={0}
          aria-label={t.seek}
          aria-valuemin={0}
          aria-valuemax={Math.round(total)}
          aria-valuenow={Math.round(time)}
          aria-valuetext={`${fmt(time)} of ${fmt(total)}`}
          onPointerDown={onBarDown}
          onPointerMove={onBarMove}
          onPointerUp={onBarUp}
          onPointerLeave={() => setHover(null)}
          className="group/bar relative flex h-5 cursor-pointer items-center"
        >
          <div className="relative h-[3px] w-full overflow-hidden rounded-full bg-white/20 transition-[height] group-hover/bar:h-[5px]">
            <div className="absolute inset-y-0 left-0 bg-white/30" style={{ width: `${bufPct}%` }} />
            <div className="absolute inset-y-0 left-0 bg-brand" style={{ width: `${pct}%` }} />
          </div>
          <span
            className={cn(
              "absolute size-3.5 -translate-x-1/2 rounded-full bg-brand shadow-[0_0_0_4px_rgb(255_46_126/0.25)] transition-transform",
              scrubbing ? "scale-100" : "scale-0 group-hover/bar:scale-100",
            )}
            style={{ left: `${pct}%` }}
          />
          {hover && (
            <span
              className="glass-strong pointer-events-none absolute bottom-full mb-2 -translate-x-1/2 rounded-md px-2 py-1 text-[12px] tabular-nums text-white"
              style={{ left: hover.x }}
            >
              {fmt(hover.t)}
            </span>
          )}
        </div>

        <div className="mt-0.5 flex items-center gap-0.5 text-white">
          <button type="button" aria-label={playing ? t.pause : t.play} onClick={toggle} className={iconBtn}>
            {playing ? <Pause size={22} className="fill-white" /> : <Play size={22} className="fill-white" />}
          </button>
          <button type="button" aria-label={t.back10} onClick={() => seekBy(-10)} className={cn(iconBtn, "max-sm:hidden")}>
            <RotateCcw size={20} />
          </button>
          <button type="button" aria-label={t.forward10} onClick={() => seekBy(10)} className={cn(iconBtn, "max-sm:hidden")}>
            <RotateCw size={20} />
          </button>

          <div className="group/vol flex items-center">
            <button
              type="button"
              aria-label={muted || volume === 0 ? t.unmute : t.mute}
              onClick={toggleMute}
              className={iconBtn}
            >
              <VolumeIcon size={22} />
            </button>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={muted ? 0 : volume}
              aria-label={t.volume}
              onChange={(e) => setVol(Number(e.target.value))}
              className="h-1 w-0 cursor-pointer appearance-none rounded-full bg-white/25 accent-white opacity-0 transition-all duration-200 group-hover/vol:mr-2 group-hover/vol:w-20 group-hover/vol:opacity-100 focus-visible:w-20 focus-visible:opacity-100 max-sm:hidden"
              style={{ backgroundImage: `linear-gradient(#fff,#fff)`, backgroundSize: `${(muted ? 0 : volume) * 100}% 100%`, backgroundRepeat: "no-repeat" }}
            />
          </div>

          <span className="ml-1.5 text-[13px] tabular-nums text-white/90">
            {fmt(time)} <span className="text-white/50">/ {fmt(total)}</span>
          </span>

          <div className="ml-auto flex items-center gap-0.5">
            <div className="relative">
              <button
                type="button"
                aria-label={t.settings}
                aria-expanded={menu !== null}
                onClick={() => setMenu((m) => (m ? null : "main"))}
                className={cn(iconBtn, menu && "bg-white/15")}
              >
                <Settings size={20} className={cn("transition-transform duration-300", menu && "rotate-45")} />
              </button>
              {menu && (
                <SettingsMenu
                  menu={menu}
                  setMenu={setMenu}
                  speed={speed}
                  quality={quality}
                  qualities={frame ? ["Auto", ...levels.map((h) => `${h}p`)] : QUALITIES}
                  playing={quality === "Auto" && height ? `${height}p` : null}
                  onSpeed={(s) => {
                    setSpeed(s);
                    if (v()) v()!.playbackRate = s;
                    setMenu(null);
                  }}
                  onQuality={(q) => {
                    setQuality(q);
                    const el = v();
                    if (el instanceof FrameMedia) el.setQuality(q === "Auto" ? 0 : parseInt(q));
                    setMenu(null);
                  }}
                />
              )}
            </div>
            {/* Picture in picture needs a click inside the frame's own page, so framed videos go without. */}
            {!frame && (
              <button
                type="button"
                aria-label={t.pip}
                onClick={() => {
                  if (document.pictureInPictureElement) document.exitPictureInPicture().catch(() => {});
                  else video.current?.requestPictureInPicture?.().catch(() => {});
                }}
                className={cn(iconBtn, "max-sm:hidden")}
              >
                <PictureInPicture2 size={20} />
              </button>
            )}
            <button
              ref={fsButton}
              type="button"
              aria-label={fullscreen ? t.exitFullscreen : t.fullscreen}
              onClick={toggleFullscreen}
              // Where the page can't go full screen (iPhone), a framed video's own button takes the tap.
              className={cn(iconBtn, fsInFrame && "pointer-events-none")}
            >
              {fullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
            </button>
          </div>
        </div>
      </div>

      {overlay && <PlayerStarted.Provider value={started}>{overlay}</PlayerStarted.Provider>}
    </div>
  );
}

function SettingsMenu({
  menu,
  setMenu,
  speed,
  quality,
  qualities,
  playing,
  onSpeed,
  onQuality,
}: {
  menu: "main" | "speed" | "quality";
  setMenu: (m: null | "main" | "speed" | "quality") => void;
  speed: number;
  quality: string;
  qualities: string[];
  /** What "Auto" is playing right now, when known. */
  playing: string | null;
  onSpeed: (s: number) => void;
  onQuality: (q: string) => void;
}) {
  const t = useUi().player;
  // "Auto" is the quality's value; shown in the page's language.
  const shown = (q: string) => (q === "Auto" ? t.auto : q);
  const row = "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-[14px] text-white/90 hover:bg-white/10";
  return (
    <div role="menu" className="glass-strong absolute bottom-full right-0 mb-3 w-60 rounded-xl p-1.5 shadow-[0_20px_60px_-15px_rgb(0_0_0/0.9)]">
      {menu === "main" && (
        <>
          <button type="button" role="menuitem" onClick={() => setMenu("quality")} className={row}>
            <span className="flex-1">{t.quality}</span>
            <span className="text-white/60">{playing ? `${t.auto} · ${playing}` : shown(quality)}</span>
            <ChevronRight size={16} className="text-white/60" />
          </button>
          <button type="button" role="menuitem" onClick={() => setMenu("speed")} className={row}>
            <span className="flex-1">{t.speed}</span>
            <span className="text-white/60">{speed === 1 ? t.normal : `${speed}×`}</span>
            <ChevronRight size={16} className="text-white/60" />
          </button>
        </>
      )}
      {menu !== "main" && (
        <>
          <button type="button" onClick={() => setMenu("main")} className={cn(row, "mb-1 border-b border-white/10 pb-3 font-medium")}>
            <ChevronLeft size={16} />
            {menu === "speed" ? t.speed : t.quality}
          </button>
          {(menu === "speed" ? SPEEDS : qualities).map((opt) => {
            const selected = menu === "speed" ? opt === speed : opt === quality;
            return (
              <button
                key={opt}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                onClick={() => (menu === "speed" ? onSpeed(opt as number) : onQuality(opt as string))}
                className={row}
              >
                <span className="grid w-4 place-items-center">{selected && <Check size={16} className="text-brand-soft" />}</span>
                {menu === "speed" ? (opt === 1 ? t.normal : `${opt}×`) : shown(opt as string)}
                {opt === "1080p" && <span className="ml-auto rounded bg-brand px-1 text-[10px] font-bold">HD</span>}
              </button>
            );
          })}
        </>
      )}
    </div>
  );
}
